/*
PURPOSE: React hook for managing @ mention detection and autocomplete in text inputs

USE CASES:
- Detect @ mention typing in comment forms
- Provide autocomplete suggestions for aligned users
- Handle mention selection and text insertion
- Track cursor position for autocomplete positioning

DOMAIN BOUNDARIES:
- Responsible for mention autocomplete state management only
- Does NOT handle text rendering (handled by textProcessor)
- Does NOT handle API calls directly (uses api.ts functions)
- Does NOT persist mention data

ASSUMPTIONS:
- Used with textarea/input elements
- Mentions trigger on @ character
- Search is debounced to avoid excessive API calls
- Only aligned users can be mentioned
*/

import { useState, useEffect, useCallback, useRef } from 'react';
import { getCurrentMention } from '../utils/mentionProcessor';
import { searchAlignedUsers } from '../api/api';

export interface MentionSuggestion {
  userId: string;
  displayName: string;
  firstName?: string;
  lastName?: string;
}

export interface UseMentionDetectionOptions {
  text: string;
  cursorPosition: number;
  onMentionInsert?: (userId: string, displayName: string) => void;
  debounceMs?: number;
}

export interface UseMentionDetectionReturn {
  showAutocomplete: boolean;
  suggestions: MentionSuggestion[];
  selectedIndex: number;
  isLoading: boolean;
  selectSuggestion: (suggestion: MentionSuggestion) => void;
  navigateUp: () => void;
  navigateDown: () => void;
  dismissAutocomplete: () => void;
}

export function useMentionDetection({
  text,
  cursorPosition,
  onMentionInsert,
  debounceMs = 300
}: UseMentionDetectionOptions): UseMentionDetectionReturn {
  const [showAutocomplete, setShowAutocomplete] = useState(false);
  const [suggestions, setSuggestions] = useState<MentionSuggestion[]>([]);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [isLoading, setIsLoading] = useState(false);

  const debounceTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Detect if user is typing a mention
  useEffect(() => {
    const mentionQuery = getCurrentMention(text, cursorPosition);

    // Clear any pending debounce
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }

    if (mentionQuery !== null) {
      // Show loading indicator IMMEDIATELY before debounce
      setShowAutocomplete(true);
      setIsLoading(true);

      // Debounce the search
      debounceTimeoutRef.current = setTimeout(async () => {
        if (mentionQuery.length === 0) {
          // Show all aligned users when just @ is typed
          // (isLoading already set above)
          try {
            const result = await searchAlignedUsers('', 10);
            const formattedSuggestions = result.users.map(user => ({
              userId: user.targetUserId,
              displayName: user.displayName || `${user.firstName || ''} ${user.lastName || ''}`.trim(),
              firstName: user.firstName,
              lastName: user.lastName
            }));
            setSuggestions(formattedSuggestions);
            setShowAutocomplete(true);
            setSelectedIndex(0);
          } catch (error) {
            console.error('Failed to fetch aligned users:', error);
            setSuggestions([]);
          } finally {
            setIsLoading(false);
          }
        } else if (mentionQuery.length >= 1) {
          // Search for users matching the query
          // (isLoading already set above)
          try {
            const result = await searchAlignedUsers(mentionQuery, 10);
            const formattedSuggestions = result.users.map(user => ({
              userId: user.targetUserId,
              displayName: user.displayName || `${user.firstName || ''} ${user.lastName || ''}`.trim(),
              firstName: user.firstName,
              lastName: user.lastName
            }));
            setSuggestions(formattedSuggestions);
            setShowAutocomplete(formattedSuggestions.length > 0);
            setSelectedIndex(0);
          } catch (error) {
            console.error('Failed to search users:', error);
            setSuggestions([]);
            setShowAutocomplete(false);
          } finally {
            setIsLoading(false);
          }
        }
      }, debounceMs);
    } else {
      // Not typing a mention - hide autocomplete
      setShowAutocomplete(false);
      setSuggestions([]);
    }

    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
    };
  }, [text, cursorPosition, debounceMs]);

  const selectSuggestion = useCallback((suggestion: MentionSuggestion) => {
    if (onMentionInsert) {
      onMentionInsert(suggestion.userId, suggestion.displayName);
    }
    setShowAutocomplete(false);
    setSuggestions([]);
  }, [onMentionInsert]);

  const navigateUp = useCallback(() => {
    setSelectedIndex(prev => (prev > 0 ? prev - 1 : suggestions.length - 1));
  }, [suggestions.length]);

  const navigateDown = useCallback(() => {
    setSelectedIndex(prev => (prev < suggestions.length - 1 ? prev + 1 : 0));
  }, [suggestions.length]);

  const dismissAutocomplete = useCallback(() => {
    setShowAutocomplete(false);
    setSuggestions([]);
  }, []);

  return {
    showAutocomplete,
    suggestions,
    selectedIndex,
    isLoading,
    selectSuggestion,
    navigateUp,
    navigateDown,
    dismissAutocomplete
  };
}
